import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Autocomplete,
  DialogActions,
  Divider,
  TextField,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import * as React from "react";

import { useAuth } from "@/providers";
import { RobotoStyledTabs, RobotoStyledTab } from "@/shared/components";
import { useNotification } from "@/shared/components/notifications/useNotification";
import {
  CollectionRecord,
  CollectionResourceRef,
  CollectionResourceType,
} from "@/shared/domain/collections";
import { useAddToCollection } from "@/shared/domain/collections/hooks/useAddToCollection";
import {
  getQueryKey,
  useCollectionsQuery,
} from "@/shared/domain/collections/hooks/useCollectionsQuery";
import { FileNode } from "@/shared/domain/files/filesystem";
import { userFacingErrorMessage } from "@/shared/http";

import styles from "./AddToCollection.module.css";
import { CreateCollection } from "./CreateCollection";

interface TabPanelProps {
  index: number;
  value: number;
  children: React.ReactElement;
}

export const TabPanel: React.FC<TabPanelProps> = ({
  index,
  value,
  children,
  ...props
}) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...props}
    >
      {value === index && (
        <div className={styles.tabPanelContent}>{children}</div>
      )}
    </div>
  );
};

interface AddToCollectionProps {
  selectedDatasets?: Set<string>;
  selectedFiles?: FileNode[];
  selectedFileIds?: Set<string>;
  selectedEvents?: Set<string>;
  close: () => void;
  onSuccess?: () => void;
}

export const AddToCollection: React.FC<AddToCollectionProps> = ({
  selectedDatasets,
  selectedFiles,
  selectedFileIds,
  selectedEvents,
  close,
  onSuccess,
}) => {
  const [collection, setCollection] = useState<CollectionRecord | null>();
  const [tabValue, setTabValue] = useState<number>(0);
  const [isMutating, setIsMutating] = useState(false);
  const [mutationError, setMutationError] = React.useState<Error | null>(null);
  const { currentOrganization } = useAuth();
  const showNotification = useNotification();
  const queryClient = useQueryClient();

  let resourceType: CollectionResourceType = "dataset";
  if (selectedEvents?.size) {
    resourceType = "event";
  } else if (selectedFiles?.length || selectedFileIds?.size) {
    resourceType = "file";
  }

  const {
    data: collectionsData,
    isLoading,
    error: fetchError,
  } = useCollectionsQuery({
    orgId: currentOrganization?.org_id,
    resourceType,
  });
  const collections = collectionsData?.items ?? [];

  const { mutate: addToCollection } = useAddToCollection();

  const updateCollection = () => {
    if (!collection?.collection_id || !currentOrganization?.org_id) {
      return;
    }

    const datasets =
      (selectedDatasets &&
        Array.from(selectedDatasets).map((dataset) => ({
          resource_type: "dataset" as const,
          resource_id: dataset,
        }))) ||
      [];

    const files =
      (selectedFiles &&
        selectedFiles.map((item) => ({
          resource_type: "file" as const,
          resource_id: item.file?.file_id || "",
        }))) ||
      [];

    const fileIds =
      (selectedFileIds &&
        Array.from(selectedFileIds).map((id) => ({
          resource_type: "file" as const,
          resource_id: id,
        }))) ||
      [];

    const events =
      (selectedEvents &&
        Array.from(selectedEvents).map((id) => ({
          resource_type: "event" as const,
          resource_id: id,
        }))) ||
      [];

    let resources: CollectionResourceRef[] = datasets;
    if (files.length) {
      resources = files;
    }
    if (fileIds.length) {
      resources = fileIds;
    }
    if (events.length) {
      resources = events;
    }

    setIsMutating(true);

    addToCollection(
      {
        collectionId: collection.collection_id,
        resourceOwnerId: currentOrganization.org_id,
        resources,
      },
      {
        onSuccess: () => {
          setIsMutating(false);
          showNotification("Added to Collection");
          onSuccess?.();
          close();
        },
        onError: (error) => {
          setIsMutating(false);
          setMutationError(error);
        },
      },
    );
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <div className={styles.form}>
      <Typography
        variant="subtitle1"
        gutterBottom
        component="div"
        className={styles.title}
      >
        Add to Collection
      </Typography>
      <Divider />
      <RobotoStyledTabs
        variant="fullWidth"
        value={tabValue}
        onChange={handleTabChange}
        aria-label="add-to-collection-tabs"
        className={styles.tabs}
      >
        <RobotoStyledTab label={"Existing Collection"} />
        <RobotoStyledTab label={"New Collection"} />
      </RobotoStyledTabs>

      <TabPanel value={tabValue} index={0}>
        <div className={styles.tabForm}>
          {fetchError && (
            <Alert
              onClose={() =>
                void queryClient.resetQueries({
                  queryKey: getQueryKey({
                    orgId: currentOrganization?.org_id,
                    resourceType,
                  }),
                })
              }
              severity="error"
            >
              {userFacingErrorMessage(fetchError)}
            </Alert>
          )}
          {mutationError && (
            <Alert onClose={() => setMutationError(null)} severity="error">
              {userFacingErrorMessage(mutationError)}
            </Alert>
          )}
          <Autocomplete
            id="available-collections"
            options={collections}
            getOptionLabel={(option) => option.name || option.collection_id}
            value={collection}
            size="small"
            loading={isLoading}
            onChange={(_, selection: CollectionRecord | null) => {
              setCollection(selection);
            }}
            renderInput={(params) => (
              <TextField {...params} placeholder="Collection" />
            )}
            componentsProps={{
              paper: {
                className: styles.autocompletePaper,
              },
            }}
          />
          <DialogActions className={styles.dialogActions}>
            <LoadingButton
              loading={isMutating}
              type="button"
              disabled={
                !collection ||
                ((!selectedEvents || selectedEvents.size === 0) &&
                  (!selectedFiles || selectedFiles.length === 0) &&
                  (!selectedFileIds || selectedFileIds.size === 0) &&
                  (!selectedDatasets || selectedDatasets.size === 0))
              }
              onClick={() => {
                void updateCollection();
              }}
              variant="outlined"
              style={{
                textTransform: "none",
              }}
              size="small"
            >
              Add to Collection (
              {selectedEvents?.size ??
                selectedFileIds?.size ??
                selectedFiles?.length ??
                selectedDatasets?.size ??
                0}
              )
            </LoadingButton>
          </DialogActions>
        </div>
      </TabPanel>
      <TabPanel value={tabValue} index={1}>
        <div className={styles.tabForm}>
          <CreateCollection
            selectedDatasets={selectedDatasets}
            selectedFiles={selectedFiles}
            selectedFileIds={selectedFileIds}
            selectedEvents={selectedEvents}
            close={close}
          />
        </div>
      </TabPanel>
    </div>
  );
};
